import {
  RunLogDocument,
  RunLogNode,
} from '../../../service/AdminSpaceLoggingService';

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;

const formatDuration = (ms: number): string => {
  if (!Number.isFinite(ms)) return '—';
  if (ms < MS_PER_SECOND) return `${Math.round(ms)}ms`;
  if (ms < MS_PER_MINUTE) return `${(ms / MS_PER_SECOND).toFixed(2)}s`;
  const minutes = Math.floor(ms / MS_PER_MINUTE);
  const seconds = ((ms % MS_PER_MINUTE) / MS_PER_SECOND).toFixed(1);
  return `${minutes}m ${seconds}s`;
};

const formatEndTime = (iso: string): string => {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleString();
};

const escapeHtml = (value: string): string =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const hasErrorAnywhere = (node: RunLogNode): boolean => {
  if (node.status === 'error') return true;
  if (node.innerSteps?.some(hasErrorAnywhere)) return true;
  if (node.subRun?.nodes?.some(hasErrorAnywhere)) return true;
  return false;
};

const jsonFence = (label: string, value: unknown): string[] => {
  if (value === undefined) return [];
  const body =
    typeof value === 'string'
      ? value
      : JSON.stringify(value, null, 2) ?? String(value);
  return ['', `**${label}**`, '', '```json', body, '```', ''];
};

interface NodeMeta {
  nodeId: string;
  startedAt: string;
  endedAt: string;
  durationMs: number;
  tokens: number;
  cost: number;
  model: string | null;
  attempts: number | null;
  status: 'success' | 'error';
  errorMessage: string | null;
}

const buildMeta = (node: RunLogNode): NodeMeta => {
  const hasModel = Boolean(node.modelProvider || node.modelId);
  const model = hasModel
    ? `${node.modelProvider ?? '?'}/${node.modelId ?? '?'}`
    : null;
  return {
    nodeId: node.nodeId,
    startedAt: node.startedAt,
    endedAt: node.endedAt,
    durationMs: node.durationMs,
    tokens: node.tokens ?? 0,
    cost: node.cost ?? 0,
    model,
    attempts: node.attempts ?? null,
    status: node.status,
    errorMessage: node.errorMessage ?? null,
  };
};

const buildSummaryHtml = (node: RunLogNode, stepNumber: string): string => {
  const name = node.label || node.nodeType;
  const parts = [
    `<span class="sc-step">${stepNumber}.</span> <span class="sc-name">${escapeHtml(
      name,
    )}</span>`,
    `<code>${escapeHtml(node.nodeType)}</code>`,
    `<span class="sc-dur">${formatDuration(node.durationMs)}</span>`,
    `<span class="sc-tok">${(node.tokens ?? 0).toLocaleString()} tok</span>`,
  ];
  if (node.status === 'error') {
    parts.push('<span class="sc-err">error</span>');
  }
  return parts.join(' <span class="sc-sep">·</span> ');
};

const appendNode = (
  node: RunLogNode,
  stepNumber: string,
  lines: string[],
  flowDepth: number,
): void => {
  const shouldOpen = hasErrorAnywhere(node);
  const openAttr = shouldOpen ? ' open' : '';
  const statusAttr =
    node.status === 'error' ? ' data-status="error"' : ' data-status="success"';
  lines.push(`<details${openAttr}${statusAttr}>`);
  lines.push('');
  lines.push(`<summary>${buildSummaryHtml(node, stepNumber)}</summary>`);
  lines.push('');
  if (node.errorMessage) {
    lines.push(`> ${node.errorMessage}`);
    lines.push('');
  }
  lines.push(...jsonFence('Input', node.input));
  lines.push(...jsonFence('Result', node.output));
  lines.push(...jsonFence('Meta', buildMeta(node)));
  if (node.rawResponse !== undefined) {
    lines.push(...jsonFence('Raw response', node.rawResponse));
  }
  if (node.innerSteps?.length) {
    lines.push('');
    lines.push('**Library inner steps**');
    lines.push('');
    node.innerSteps.forEach((inner, i) => {
      appendNode(inner, `${stepNumber}.${i + 1}`, lines, flowDepth);
    });
  }
  if (node.subRun) {
    lines.push('');
    appendFlow(node.subRun, lines, flowDepth + 1);
    lines.push('');
  }
  lines.push('</details>');
  lines.push('');
};

const buildSubflowBanner = (doc: RunLogDocument): string => {
  const parts = [
    '<span class="sc-subflow-badge">FLOW</span>',
    '<span class="sc-subflow-arrow">→</span>',
    `<span class="sc-subflow-name">${escapeHtml(doc.rootFlowName)}</span>`,
    `<code class="sc-subflow-id">${escapeHtml(doc.rootFlowId)}</code>`,
    `<span class="sc-subflow-stats">${formatDuration(
      doc.durationMs,
    )} · ${doc.totalTokens.toLocaleString()} tok</span>`,
  ];
  if (doc.status === 'error') {
    parts.push('<span class="sc-err">error</span>');
  }
  return parts.join(' ');
};

const appendRootFlow = (doc: RunLogDocument, lines: string[]): void => {
  lines.push('<div class="sc-root-banner">');
  lines.push('');
  lines.push('<p class="sc-root-eyebrow">▶ Entry agent</p>');
  lines.push('');
  lines.push(`## ${escapeHtml(doc.rootFlowName)}`);
  lines.push('');
  const statsHtml = [
    `<code class="sc-root-id">${escapeHtml(doc.rootFlowId)}</code>`,
    `<span class="sc-root-stat">status <strong>${doc.status}</strong></span>`,
    `<span class="sc-root-stat">${formatDuration(doc.durationMs)}</span>`,
    `<span class="sc-root-stat">${doc.totalTokens.toLocaleString()} tok</span>`,
    `<span class="sc-root-stat">$${doc.totalCost.toFixed(4)}</span>`,
  ];
  lines.push(`<p class="sc-root-sub">${statsHtml.join(' ')}</p>`);
  lines.push('');
  lines.push('</div>');
  lines.push('');
  if (doc.errorMessage) {
    lines.push(`> ${doc.errorMessage}`);
    lines.push('');
  }
  doc.nodes.forEach((node, i) => {
    appendNode(node, `${i + 1}`, lines, 0);
  });
};

const appendSubflow = (
  doc: RunLogDocument,
  lines: string[],
  depth: number,
): void => {
  const statusAttr = doc.status === 'error' ? ' data-status="error"' : '';
  lines.push(
    `<details open class="sc-subflow-block" data-flow-depth="${depth}"${statusAttr}>`,
  );
  lines.push('');
  lines.push(
    `<summary class="sc-subflow-head">${buildSubflowBanner(doc)}</summary>`,
  );
  lines.push('');
  if (doc.errorMessage) {
    lines.push(`> ${doc.errorMessage}`);
    lines.push('');
  }
  doc.nodes.forEach((node, i) => {
    appendNode(node, `${i + 1}`, lines, depth);
  });
  lines.push('</details>');
  lines.push('');
};

const appendFlow = (
  doc: RunLogDocument,
  lines: string[],
  depth: number,
): void => {
  if (depth === 0) {
    appendRootFlow(doc, lines);
    return;
  }
  appendSubflow(doc, lines, depth);
};

export const buildRunLogBodyMarkdown = (doc: RunLogDocument): string => {
  const lines: string[] = [];
  appendFlow(doc, lines, 0);
  return lines.join('\n');
};

export const buildRunLogMarkdown = (doc: RunLogDocument): string => {
  const headerBits = [
    `**Process** \`${doc.processId}\``,
    `**Org** ${escapeHtml(doc.orgName ?? doc.orgId ?? '—')}`,
    `**Space** ${escapeHtml(doc.spaceName ?? doc.spaceId)}`,
    `**End time** ${formatEndTime(doc.endedAt)}`,
  ];
  const header = [headerBits.join(' · '), '', '---', ''].join('\n');
  return `${header}\n${buildRunLogBodyMarkdown(doc)}`;
};

export const formatNodeDuration = formatDuration;
